/**
 * Semilla OFFLINE del léxico Strong (concordancia) — subconjunto de arranque.
 *
 * Cubre las palabras de los versículos que se siembran ya etiquetados con su
 * número Strong (ver `strong-tagged-verses.ts`): Génesis 1:3 (hebreo) y Juan 1:1
 * (griego), más algunas entradas vecinas de apertura de AT/NT. Es suficiente para
 * que la concordancia funcione SIN servidor desde el primer arranque; el léxico
 * completo puede sincronizarse luego.
 *
 * Fuente: «Strong's Exhaustive Concordance of the Bible» (1890), de DOMINIO
 * PÚBLICO — números, lemas, transliteraciones y glosas. Las definiciones son
 * resúmenes propios derivados de esa obra de dominio público.
 *
 * Este módulo es DATA (léxico de dominio público), no cadenas de UI: se desactiva
 * `lingui/no-unlocalized-strings` a nivel de archivo, igual que en `reader/data`.
 */
/* eslint-disable lingui/no-unlocalized-strings */

interface StrongLexiconEntry {
  /** Número Strong canónico, p. ej. "H7225" (hebreo) o "G3056" (griego). */
  code: string;
  /** Lema en el idioma original (hebreo/arameo o griego). */
  lemma: string;
  /** Transliteración latina del lema. */
  transliteration: string;
  /** Pronunciación aproximada. */
  pronunciation: string;
  /** Glosa breve (traducción/significado de cabecera). */
  gloss: string;
  /** Definición más extensa (texto plano de dominio público). */
  definition: string;
  /** "hebrew" para AT, "greek" para NT. */
  language: "hebrew" | "greek";
}

export const STRONG_LEXICON: StrongLexiconEntry[] = [
  // ── Hebreo (Antiguo Testamento) — Génesis 1:1 ──────────────────────────
  {
    code: "H7225",
    lemma: "רֵאשִׁית",
    transliteration: "reshith",
    pronunciation: "ray-sheeth'",
    gloss: "beginning, first",
    definition:
      "The first, in place, time, order or rank (specifically a firstfruit): beginning, chief, first(-fruits, part, time), principal thing.",
    language: "hebrew",
  },
  {
    code: "H1254",
    lemma: "בָּרָא",
    transliteration: "bara",
    pronunciation: "baw-raw'",
    gloss: "to create, shape",
    definition:
      "A primitive root; (absolutely) to create; (qualified) to cut down (a wood), select, feed (as formative processes): choose, create (creator), cut down, dispatch, do, make (fat).",
    language: "hebrew",
  },
  {
    code: "H430",
    lemma: "אֱלֹהִים",
    transliteration: "elohim",
    pronunciation: "el-o-heem'",
    gloss: "God, gods",
    definition:
      "Plural of H433; gods in the ordinary sense; but specifically used (in the plural thus, especially with the article) of the supreme God: angels, exceeding, God (gods)(-dess, -ly), (very) great, judges, mighty.",
    language: "hebrew",
  },
  {
    code: "H8064",
    lemma: "שָׁמַיִם",
    transliteration: "shamayim",
    pronunciation: "shaw-mah'-yim",
    gloss: "heaven, sky",
    definition:
      "The sky (as aloft; the dual perhaps alluding to the visible arch in which the clouds move, as well as to the higher ether where the celestial bodies revolve): air, astrologer, heaven(-s).",
    language: "hebrew",
  },
  {
    code: "H776",
    lemma: "אֶרֶץ",
    transliteration: "erets",
    pronunciation: "eh'-rets",
    gloss: "earth, land",
    definition:
      "From an unused root probably meaning to be firm; the earth (at large, or partitively a land): common, country, earth, field, ground, land, nations, way, wilderness, world.",
    language: "hebrew",
  },
  {
    code: "H559",
    lemma: "אָמַר",
    transliteration: "amar",
    pronunciation: "aw-mar'",
    gloss: "to say, speak",
    definition:
      "A primitive root; to say (used with great latitude): answer, appoint, avouch, bid, boast self, call, certify, challenge, charge, command, commune, consider, declare, demand, intend, name, publish, report, require, say, speak, tell, utter.",
    language: "hebrew",
  },
  {
    code: "H216",
    lemma: "אוֹר",
    transliteration: "owr",
    pronunciation: "ore",
    gloss: "light",
    definition:
      "From H215; illumination or (concretely) luminary (in every sense, including lightning, happiness, etc.): bright, clear, day, light(-ning), morning, sun.",
    language: "hebrew",
  },

  // ── Griego (Nuevo Testamento) — Juan 1:1 y Juan 3:16 ───────────────────
  {
    code: "G746",
    lemma: "ἀρχή",
    transliteration: "archē",
    pronunciation: "ar-khay'",
    gloss: "beginning, origin",
    definition:
      "From G756; (properly abstract) a commencement, or (concretely) chief (in various applications of order, time, place or rank): beginning, corner, (at the, the) first (estate), magistrate, power, principality, principle, rule.",
    language: "greek",
  },
  {
    code: "G3056",
    lemma: "λόγος",
    transliteration: "logos",
    pronunciation: "log'-os",
    gloss: "word, the Word",
    definition:
      "From G3004; something said (including the thought); by implication a topic (subject of discourse), also reasoning (the mental faculty) or motive; by extension a computation; specifically (with the article in John) the Divine Expression (that is, Christ): account, cause, communication, word.",
    language: "greek",
  },
  {
    code: "G2316",
    lemma: "θεός",
    transliteration: "theos",
    pronunciation: "theh'-os",
    gloss: "God, a god",
    definition:
      "Of uncertain affinity; a deity, especially (with G3588) the supreme Divinity; figuratively a magistrate; by Hebraism very: exceeding, God, god(-ly, -ward).",
    language: "greek",
  },
  {
    code: "G25",
    lemma: "ἀγαπάω",
    transliteration: "agapaō",
    pronunciation: "ag-ap-ah'-o",
    gloss: "to love",
    definition:
      "Perhaps from agan (much) (or compare H5689); to love (in a social or moral sense): (be-) love(-ed). Compare G5368.",
    language: "greek",
  },
  {
    code: "G2889",
    lemma: "κόσμος",
    transliteration: "kosmos",
    pronunciation: "kos'-mos",
    gloss: "world, order",
    definition:
      "Probably from the base of G2865; orderly arrangement, that is, decoration; by implication the world (in a wide or narrow sense, including its inhabitants, literally or figuratively (morally)): adorning, world.",
    language: "greek",
  },
  {
    code: "G5207",
    lemma: "υἱός",
    transliteration: "huios",
    pronunciation: "hwee-os'",
    gloss: "son",
    definition:
      "Apparently a primary word; a son (sometimes of animals), used very widely of immediate, remote or figuratively kinship: child, foal, son.",
    language: "greek",
  },
  {
    code: "G2222",
    lemma: "ζωή",
    transliteration: "zōē",
    pronunciation: "dzo-ay'",
    gloss: "life",
    definition: "From G2198; life (literally or figuratively): life(-time). Compare G5590.",
    language: "greek",
  },
];
